import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ArrowLeft, Send, UserCheck, Bot } from 'lucide-react';

export default function ConversationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const socket = useSocket();
  const [conversation, setConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversation = () => {
    api.get(`/conversations/${id}`).then(setConversation).catch(() => toast.error('Konuşma yüklenemedi'));
  };

  useEffect(() => { fetchConversation(); }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  useEffect(() => {
    if (!socket) return;
    socket.on('new-message', (data) => {
      if (data.conversationId === id) fetchConversation();
    });
    return () => socket.off('new-message');
  }, [socket, id]);

  const handleTakeover = async () => {
    try {
      await api.post(`/conversations/${id}/takeover`);
      toast.success('Konuşma devralındı');
      fetchConversation();
    } catch { toast.error('Devralma başarısız'); }
  };

  const handleRelease = async () => {
    try {
      await api.post(`/conversations/${id}/release`);
      toast.success('Konuşma bota bırakıldı');
      fetchConversation();
    } catch { toast.error('Hata oluştu'); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${id}/messages`, { content: message });
      setMessage('');
      fetchConversation();
    } catch { toast.error('Mesaj gönderilemedi'); }
    finally { setSending(false); }
  };

  if (!conversation) return <div style={{ padding: 28, color: '#94a3b8' }}>Yükleniyor...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-secondary" onClick={() => navigate('/conversations')} style={{ padding: '6px 10px' }}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{conversation.customerName || 'Bilinmeyen Müşteri'}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {conversation.channel} · {conversation.stage} · {conversation.language?.toUpperCase()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {conversation.isTakenOver ? (
            <button className="btn btn-secondary" onClick={handleRelease} style={{ fontSize: 13 }}>
              <Bot size={15} /> Bota Bırak
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleTakeover} style={{ fontSize: 13 }}>
              <UserCheck size={15} /> Devral
            </button>
          )}
          {conversation.isTakenOver && (
            <span className="badge badge-yellow" style={{ alignSelf: 'center' }}>Canlı Mod</span>
          )}
        </div>
      </div>

      {/* Mesajlar */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {conversation.messages.map(msg => {
          const isBot = msg.role === 'ASSISTANT';
          const images = (() => { try { return JSON.parse(msg.images || '[]'); } catch { return []; } })();
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'USER' ? 'flex-start' : 'flex-end' }}>
              <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: msg.role === 'USER' ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                  background: msg.role === 'USER' ? '#f1f5f9' : '#3b82f6',
                  color: msg.role === 'USER' ? '#1e293b' : 'white',
                  fontSize: 14, lineHeight: 1.5,
                }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: 'right' }}>
                    {format(new Date(msg.sentAt), 'HH:mm', { locale: tr })}
                  </div>
                </div>
                {images.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: isBot ? 'flex-end' : 'flex-start' }}>
                    {images.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer">
                        <img
                          src={url}
                          alt={`oda-${i + 1}`}
                          style={{ width: 160, height: 110, objectFit: 'cover', borderRadius: 10, border: '2px solid #e2e8f0', cursor: 'pointer' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Mesaj gönder (sadece devralındıysa) */}
      {conversation.isTakenOver && (
        <form onSubmit={handleSend} style={{ padding: '12px 24px', background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 10 }}>
          <input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Müşteriye mesaj yaz..."
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary" disabled={sending || !message.trim()}>
            <Send size={16} /> {sending ? 'Gönderiliyor' : 'Gönder'}
          </button>
        </form>
      )}
      {!conversation.isTakenOver && (
        <div style={{ padding: '10px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
          Bot bu konuşmayı yönetiyor. Mesaj göndermek için konuşmayı devralın.
        </div>
      )}
    </div>
  );
}
